import type { NodeConfig } from './node-config.schema';
import { DEFAULT_3D_NODE_STATE, type ThreeDNodeState } from './3d-node-state.schema';

/**
 * Default Node Configurations
 * Single source of truth for all node type defaults
 */

/**
 * Node Title Templates
 * Used to generate automatic titles when creating nodes
 * Format: "{BaseName} {Number}"
 */
export const NODE_TITLE_TEMPLATES: Record<string, string> = {
  'text-file': 'Text File',
  'image-file': 'Image File',
  'video-file': 'Video File',
  'audio-file': 'Audio File',
  'text': 'Text node',
  'video': 'Video node',
  'video-create': 'Video Create',
  'video-edit': 'Video Edit',
  'video-enhance': 'Video Enhance',
  'video-tools': 'Video Tools',
  '3d': '3D node',
  'shader': 'Shader node',
  'data-table': 'Data Table',
  'variation-generator': 'Variation Generator',
  'prompt-composer': 'Prompt Composer',
  'batch-generator': 'Batch Generator',
  'gallery-group': 'Gallery',
  'gallery-container': 'Gallery Container',
  'comment': 'Comment',
  'crop': 'Crop node',
  'mask': 'Mask node',
  'upscale': 'Upscale node',
  'outpaint': 'Outpaint node',
  'controlnet': 'ControlNet node',
  'levels': 'Levels node',
  'blur': 'Blur node',
  'background-remover': 'Background remover node',
  'lip-sync': 'Lips sync node',
  'describer': 'Describer node',
  'extract-video-frame': 'Extract video frame node',
  'image-create': 'Image Create',
  'image-edit': 'Image Edit',
  'image-enhance': 'Image Enhance',
  'image-utils': 'Image Utils',
  'lora-model': 'LoRA',
} as const;

/**
 * Node Visual Constants
 * Centralized visual properties for consistent UI
 */
export const NODE_VISUAL_CONSTANTS = {
  // Border radius values
  borderRadius: {
    card: 14,        // Main card border radius
    button: {
      small: 6,      // Small buttons (collapse, close)
      medium: 8,     // Medium buttons (footer actions)
      large: 10,     // Large buttons (upload zones)
    },
  },

  // Title dimensions (NodeTitle component: icon 16px + gap 8px + text ~16px + mb-2 8px)
  title: {
    height: 28,      // Total height including bottom margin
    iconSize: 16,    // Icon height
    gap: 8,          // Gap between icon and text
    marginBottom: 8, // Bottom margin (mb-2)
  },

  // Collapsed state
  collapsed: {
    height: 60,      // Height of collapsed node
  },

  // Textarea constraints
  textarea: {
    minHeight: 160,  // Minimum textarea height
    maxHeight: 500,  // Maximum textarea height
  },

  // Node limits
  limits: {
    maxImages: 3,    // Maximum number of images per AI node
  },
} as const;

/**
 * Default Dimensions per Node Type
 * Square is the default shape, not an aspect lock. Both axes seed a new node.
 * A manual corner resize is stored on the node and is not replaced when
 * content arrives. Content with an aspect ratio may auto-size a node that
 * has not been manually resized into landscape or portrait.
 */
export const NODE_SIZE = 500;

export const NODE_DIMENSIONS: Record<string, { width: number; height: number }> = {
  TextFile: { width: NODE_SIZE, height: NODE_SIZE },
  ImageFile: { width: NODE_SIZE, height: NODE_SIZE },
  VideoFile: { width: NODE_SIZE, height: NODE_SIZE },
  AudioFile: { width: NODE_SIZE, height: NODE_SIZE },
  VideoNode: { width: NODE_SIZE, height: NODE_SIZE },
  ImageNode: { width: NODE_SIZE, height: NODE_SIZE },
  TextNode: { width: NODE_SIZE, height: NODE_SIZE },
  FrameInput: { width: NODE_SIZE, height: NODE_SIZE },
  FrameOutput: { width: NODE_SIZE, height: NODE_SIZE },
  ShaderNode: { width: NODE_SIZE, height: NODE_SIZE },
  Node3D: { width: NODE_SIZE, height: NODE_SIZE },
  DataTable: { width: NODE_SIZE, height: NODE_SIZE },
  VariationGenerator: { width: NODE_SIZE, height: NODE_SIZE },
  PromptComposer: { width: NODE_SIZE, height: NODE_SIZE },
  BatchGenerator: { width: NODE_SIZE, height: NODE_SIZE },
  GalleryGroup: { width: 2200, height: 2200 },
  GalleryContainer: { width: NODE_SIZE, height: NODE_SIZE },
  CommentNode: { width: 44, height: 44 },
  CropNode: { width: NODE_SIZE, height: NODE_SIZE },
  MaskNode: { width: NODE_SIZE, height: NODE_SIZE },
  UpscaleNode: { width: NODE_SIZE, height: NODE_SIZE },
  OutpaintNode: { width: NODE_SIZE, height: NODE_SIZE },
  ControlNetNode: { width: NODE_SIZE, height: NODE_SIZE },
  LevelsNode: { width: NODE_SIZE, height: NODE_SIZE },
  BlurNode: { width: NODE_SIZE, height: NODE_SIZE },
  BackgroundRemoverNode: { width: NODE_SIZE, height: NODE_SIZE },
  LipSyncNode: { width: NODE_SIZE, height: NODE_SIZE },
  DescriberNode: { width: NODE_SIZE, height: NODE_SIZE },
  ExtractVideoFrameNode: { width: NODE_SIZE, height: NODE_SIZE },
  ImageCreateNode: { width: NODE_SIZE, height: NODE_SIZE },
  ImageEditNode: { width: NODE_SIZE, height: NODE_SIZE },
  ImageEnhanceNode: { width: NODE_SIZE, height: NODE_SIZE },
  ImageUtilsNode: { width: NODE_SIZE, height: NODE_SIZE },
  LoraModel: { width: 280, height: 280 },
} as const;

export const NODE_DEFAULTS: Record<string, NodeConfig> = {
  'text-file': {
    type: 'text-file',
    title: 'Text File',
    icon: 'file-text',
    fields: [
      {
        label: 'Content',
        type: 'prompt',
        placeholder: 'Enter text content...',
        active: true,
      }
    ],
    parentConfig: {
      canBeParent: false,   // File nodes can't have children
      canHaveParent: true,  // Can be generated by AI nodes
    },
  },

  'image-file': {
    type: 'image-file',
    title: 'Image File',
    icon: 'image',
    fields: [
      {
        label: 'Image',
        type: 'image',
        active: true,
      }
    ],
    parentConfig: {
      canBeParent: false,   // File nodes can't have children
      canHaveParent: true,  // Can be generated by AI nodes
    },
  },

  'audio-file': {
    type: 'audio-file',
    title: 'Audio File',
    icon: 'music',
    fields: [
      {
        label: 'Audio',
        type: 'audio',
        active: true,
      }
    ],
    parentConfig: {
      canBeParent: false,
      canHaveParent: true,
    },
  },

  'video-file': {
    type: 'video-file',
    title: 'Video File',
    icon: 'video',
    fields: [
      {
        label: 'Video',
        type: 'video',
        active: true,
      }
    ],
    parentConfig: {
      canBeParent: false,
      canHaveParent: true,
    },
  },

  text: {
    type: 'text',
    title: 'Text node',
    icon: 'file-text',
    fields: [
      {
        label: 'Prompt',
        type: 'prompt',
        placeholder: 'Enter your text prompt...',
        active: true,
      },
      {
        label: 'Image',
        type: 'image',
        active: true,
      },
    ],
    parentConfig: {
      canBeParent: false,   // AI generator nodes don't use childNodeIds (items are temporary)
      canHaveParent: false, // Generator nodes are always root
    },
  },

  image: {
    type: 'image',
    title: 'Image Generator',
    icon: 'image',
    fields: [
      {
        label: 'Prompt',
        type: 'prompt',
        placeholder: 'Describe the image you want...',
        active: true,
      },
      {
        label: 'Input Image',
        type: 'image',
        active: true,
      },
    ],
    parentConfig: {
      canBeParent: false,   // AI generator nodes don't use childNodeIds (items are temporary)
      canHaveParent: false, // Generator nodes are always root
    },
  },

  video: {
    type: 'video',
    title: 'Video Generator',
    icon: 'video',
    fields: [
      {
        label: 'Prompt',
        type: 'prompt',
        placeholder: 'Describe the video scene...',
        active: true,
      },
      {
        label: 'Input Image',
        type: 'image',
        active: true,
      },
    ],
    parentConfig: {
      canBeParent: false,
      canHaveParent: false,
    },
  },

  'video-create': {
    type: 'video',
    title: 'Video Create',
    icon: 'sparkles',
    fields: [
      {
        label: 'Prompt',
        type: 'prompt',
        placeholder: 'Describe the video you want to create...',
        active: true,
      },
      {
        label: 'Reference Frame',
        type: 'image',
        active: true,
      },
    ],
    parentConfig: {
      canBeParent: false,
      canHaveParent: false,
    },
  },

  'video-edit': {
    type: 'video',
    title: 'Video Edit',
    icon: 'pen-tool',
    fields: [
      {
        label: 'Input Video',
        type: 'video',
        active: true,
      },
      {
        label: 'Edit Prompt',
        type: 'prompt',
        placeholder: 'Describe what to change in the video...',
        active: true,
      },
    ],
    parentConfig: {
      canBeParent: false,
      canHaveParent: false,
    },
  },

  'video-enhance': {
    type: 'video',
    title: 'Video Enhance',
    icon: 'wand-2',
    fields: [
      {
        label: 'Input Video',
        type: 'video',
        active: true,
      },
      {
        label: 'Instructions',
        type: 'prompt',
        placeholder: 'Optional: describe how to enhance...',
        active: true,
      },
    ],
    parentConfig: {
      canBeParent: false,
      canHaveParent: false,
    },
  },

  'video-tools': {
    type: 'video',
    title: 'Video Tools',
    icon: 'wrench',
    fields: [
      {
        label: 'Input Video',
        type: 'video',
        active: true,
      },
      {
        label: 'Instructions',
        type: 'prompt',
        placeholder: 'Optional: additional instructions...',
        active: true,
      },
    ],
    parentConfig: {
      canBeParent: false,
      canHaveParent: false,
    },
  },

  '3d': {
    type: '3d',
    title: '3D Model',
    icon: 'box',
    fields: [
      {
        label: 'Prompt',
        type: 'prompt',
        placeholder: 'Describe the 3D model...',
        active: true,
      },
      {
        label: 'Reference',
        type: 'image',
      },
    ],
  },

  shader: {
    type: 'shader',
    title: 'Shader node',
    icon: 'image',
    fields: [
      {
        label: 'Shader Idea',
        type: 'prompt',
        placeholder: 'Enter your shader idea...',
        active: true,
      },
      {
        label: 'Reference Image',
        type: 'image',
        active: true,
      },
    ],
  },

  'data-table': {
    type: 'data-table',
    title: 'Data Table',
    icon: 'table',
    fields: [],
    parentConfig: {
      canBeParent: false,
      canHaveParent: false,
    },
  },

  'variation-generator': {
    type: 'variation-generator',
    title: 'Variation Generator',
    icon: 'shuffle',
    fields: [],
    parentConfig: {
      canBeParent: false,
      canHaveParent: false,
    },
  },

  'prompt-composer': {
    type: 'prompt-composer',
    title: 'Prompt Composer',
    icon: 'pen-line',
    fields: [],
    parentConfig: {
      canBeParent: false,
      canHaveParent: false,
    },
  },

  'batch-generator': {
    type: 'batch-generator',
    title: 'Batch Generator',
    icon: 'play',
    fields: [],
    parentConfig: {
      canBeParent: false,
      canHaveParent: false,
    },
  },

  'gallery-group': {
    type: 'gallery-group',
    title: 'Gallery',
    icon: 'layout-grid',
    fields: [],
    parentConfig: {
      canBeParent: true,
      canHaveParent: false,
    },
  },

  comment: {
    type: 'comment',
    title: 'Comment',
    icon: 'message-circle',
    fields: [],
    parentConfig: {
      canBeParent: false,
      canHaveParent: true,
    },
  },

  crop: {
    type: 'crop',
    title: 'Crop node',
    icon: 'crop',
    fields: [
      {
        label: 'Input Image',
        type: 'image',
        active: true,
      },
      {
        label: 'Input Video',
        type: 'video',
        active: true,
      },
    ],
  },

  mask: {
    type: 'mask',
    title: 'Mask node',
    icon: 'paintbrush',
    fields: [
      {
        label: 'Input Image',
        type: 'image',
        active: true,
      },
    ],
  },

  upscale: {
    type: 'upscale',
    title: 'Upscale node',
    icon: 'maximize-2',
    fields: [
      {
        label: 'How to upscale',
        type: 'prompt',
        placeholder: 'Describe how to upscale...',
        active: true,
      },
      {
        label: 'Input Image',
        type: 'image',
        active: true,
      },
      {
        label: 'Input Video',
        type: 'video',
        active: true,
      },
    ],
  },

  outpaint: {
    type: 'outpaint',
    title: 'Outpaint node',
    icon: 'maximize',
    fields: [
      {
        label: 'What to add to image',
        type: 'prompt',
        placeholder: 'Describe what to add...',
        active: true,
      },
      {
        label: 'Input Image',
        type: 'image',
        active: true,
      },
      {
        label: 'Input Video',
        type: 'video',
        active: true,
      },
    ],
  },

  controlnet: {
    type: 'controlnet',
    title: 'ControlNet node',
    icon: 'cpu',
    fields: [],
  },

  levels: {
    type: 'levels',
    title: 'Levels node',
    icon: 'sliders-horizontal',
    fields: [
      {
        label: 'Input Image',
        type: 'image',
        active: true,
      },
      {
        label: 'Input Video',
        type: 'video',
        active: true,
      },
    ],
  },

  blur: {
    type: 'blur',
    title: 'Blur node',
    icon: 'sun-dim',
    fields: [
      {
        label: 'Prompt',
        type: 'prompt',
        placeholder: 'Describe blur settings...',
        active: true,
      },
      {
        label: 'Input Image',
        type: 'image',
        active: true,
      },
    ],
  },

  'background-remover': {
    type: 'background-remover',
    title: 'Background remover node',
    icon: 'scan',
    fields: [
      {
        label: 'Input Image',
        type: 'image',
        active: true,
      },
    ],
  },

  'lip-sync': {
    type: 'lip-sync',
    title: 'Lips sync node',
    icon: 'lips',
    fields: [
      {
        label: 'Input Video',
        type: 'video',
        active: true,
      },
      {
        label: 'Input Audio',
        type: 'file',
        active: true,
      },
    ],
  },

  'describer': {
    type: 'describer',
    title: 'Describer node',
    icon: 'clipboard-pen',
    fields: [
      {
        label: 'Input Image',
        type: 'image',
        active: true,
      },
      {
        label: 'Input Video',
        type: 'video',
        active: true,
      },
    ],
  },

  'extract-video-frame': {
    type: 'extract-video-frame',
    title: 'Extract video frame node',
    icon: 'image-play',
    fields: [
      {
        label: 'Input Video',
        type: 'video',
        active: true,
      },
    ],
  },

  'image-create': {
    type: 'image',
    title: 'Image Create',
    icon: 'sparkles',
    fields: [
      {
        label: 'Prompt',
        type: 'prompt',
        placeholder: 'Describe the image you want to create...',
        active: true,
      },
      {
        label: 'Input Image',
        type: 'image',
        active: true,
      },
    ],
    parentConfig: {
      canBeParent: false,
      canHaveParent: false,
    },
  },

  'image-edit': {
    type: 'image',
    title: 'Image Edit',
    icon: 'pen-tool',
    fields: [
      {
        label: 'Input Image',
        type: 'image',
        active: true,
      },
      {
        label: 'Edit Prompt',
        type: 'prompt',
        placeholder: 'Describe what to change in the image...',
        active: true,
      },
    ],
    parentConfig: {
      canBeParent: false,
      canHaveParent: false,
    },
  },

  'image-enhance': {
    type: 'image',
    title: 'Image Enhance',
    icon: 'wand-2',
    fields: [
      {
        label: 'Input Image',
        type: 'image',
        active: true,
      },
      {
        label: 'Instructions',
        type: 'prompt',
        placeholder: 'Optional: describe how to enhance...',
        active: true,
      },
    ],
    parentConfig: {
      canBeParent: false,
      canHaveParent: false,
    },
  },

  'image-utils': {
    type: 'image',
    title: 'Image Utils',
    icon: 'wrench',
    fields: [
      {
        label: 'Input Image',
        type: 'image',
        active: true,
      },
      {
        label: 'Instructions',
        type: 'prompt',
        placeholder: 'Optional: additional instructions...',
        active: true,
      },
    ],
    parentConfig: {
      canBeParent: false,
      canHaveParent: false,
    },
  },

  'lora-model': {
    type: 'lora-model',
    title: 'LoRA',
    icon: 'box',
    fields: [],
    parentConfig: {
      canBeParent: false,
      canHaveParent: false,
    },
  },
};

/**
 * Default 3D Node State Data
 * Used when creating new 3D nodes
 */
export const defaultThreeDNodeData: ThreeDNodeState = {
  ...DEFAULT_3D_NODE_STATE,
  title: '3D Model Generator',
} as ThreeDNodeState;
